"use client";

import { useLocale as useUrlLocale } from "next-intl";
import { DEFAULT_LOCALE, isLocale } from "@/lib/locale";
import type { Locale } from "@/lib/locale";

/**
 * Active UI locale for **data labels** (categories, provinces, languages,
 * client types, availability, …) in client components.
 *
 * Delegates to next-intl's **URL locale** — the single source of truth since
 * the app moved to URL-based routing (`localeDetection: false`, "the URL
 * decides the locale, nothing else"; see the LocaleLink notes). It is
 * context-based, so it stays static-safe (no `usePathname`/`useSearchParams`)
 * and returns the same value at SSR, first client render, and after mount —
 * no hydration flash.
 *
 * History: this used to read the separate `et_lang` cookie/localStorage, a
 * PARALLEL locale source that could desync from the URL — rendering VI data
 * labels on an EN page (and vice versa) whenever the two drifted apart (e.g.
 * navigating by URL rather than the switcher). Unifying on the URL locale
 * closes that seam for every consumer at once.
 *
 * `initial` is kept for backwards-compat with existing call sites (which seed
 * it from the server) and used only as a fallback if next-intl ever yields an
 * unknown value; the URL locale otherwise wins.
 */
export function useLocale(initial: Locale = DEFAULT_LOCALE): Locale {
  const locale = useUrlLocale();
  return isLocale(locale) ? locale : initial;
}
